import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './styles.scss';
import { updateFacility } from '../../../../services/facility';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import { MultiSelect } from '../../../../components/other/MultiSelect';
import { getCompanyUserGroupList } from '../../../../services/company';
import Select from 'react-select';
import { SCHEDULING_HOURS_OPTIONS, SCHEDULING_MINUTES_OPTIONS, MAX_WORKING_HOURS_PERIOD_OPTIONS } from '../../../../constants';

const SettingsTab = ({ facility, setFacility }) => {
  const [userGroups, setUserGroups] = useState([]);

  const getUserGroups = useCallback(async () => {
    try {
      const params = {
        get_all: true,
        facilities_ids: [facility.id],
      };
      const response = await getCompanyUserGroupList(facility.company.id, params);
      if (response) {
        setUserGroups(response);
      }
    } catch (e) {
      console.log(e);
    }
  }, [facility.company.id]);

  useEffect(() => {
    getUserGroups();
  }, [getUserGroups]);

  const userGroupsOptions = useMemo(() => {
    return userGroups.map((item) => ({ value: item.id, label: item.name }));
  }, [userGroups]);

  const initialFormData = useMemo(() => {
    return {
      allow_cna_module: facility.allow_cna_module,
      is_tabula_sync_on: facility.is_tabula_sync_on,
      nurses_hours: facility.nurses_hours_per_shift
        ? SCHEDULING_HOURS_OPTIONS.find(({ value }) => value === facility.nurses_hours_per_shift)
        : SCHEDULING_HOURS_OPTIONS.find(({ value }) => value === 8),
      nurses_minutes: facility.nurses_minutes_per_shift
        ? SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value === facility.nurses_minutes_per_shift)
        : SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value === 0),
      cna_hours: facility.cna_hours_per_shift
        ? SCHEDULING_HOURS_OPTIONS.find(({ value }) => value === facility.cna_hours_per_shift)
        : SCHEDULING_HOURS_OPTIONS.find(({ value }) => value === 8),
      cna_minutes: facility.cna_minutes_per_shift
        ? SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value === facility.cna_minutes_per_shift)
        : SCHEDULING_MINUTES_OPTIONS.find(({ value }) => value === 0),
      max_working_hours: facility.max_working_hours ?? 8,
      max_working_hours_period:
        MAX_WORKING_HOURS_PERIOD_OPTIONS.find(({ value }) => value === facility.max_working_hours_period) ||
        MAX_WORKING_HOURS_PERIOD_OPTIONS.find(({ value }) => value === 'per_day'),
      allow_bedboard_module: facility.allow_bedboard_module,
      shift_1_start: facility.shift_1_start
        ? new Date(new Date().setHours(...facility.shift_1_start.split(':').map(Number), 0, 0))
        : undefined,
      shift_1_end: facility.shift_1_end ? new Date(new Date().setHours(...facility.shift_1_end.split(':').map(Number), 0, 0)) : undefined,
      shift_2_start: facility.shift_2_start
        ? new Date(new Date().setHours(...facility.shift_2_start.split(':').map(Number), 0, 0))
        : undefined,
      shift_2_end: facility.shift_2_end ? new Date(new Date().setHours(...facility.shift_2_end.split(':').map(Number), 0, 0)) : undefined,
      shift_3_start: facility.shift_3_start
        ? new Date(new Date().setHours(...facility.shift_3_start.split(':').map(Number), 0, 0))
        : undefined,
      shift_3_end: facility.shift_3_end ? new Date(new Date().setHours(...facility.shift_3_end.split(':').map(Number), 0, 0)) : undefined,
      nurses_selection:
        facility.nurses_selection && userGroups
          ? userGroups.filter((item) => facility.nurses_selection.includes(item.id)).map((item) => ({ value: item.id, label: item.name }))
          : [],
      cna_selection:
        facility.cna_selection && userGroups
          ? userGroups.filter((item) => facility.cna_selection.includes(item.id)).map((item) => ({ value: item.id, label: item.name }))
          : [],
    };
  }, [facility, userGroups]);

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    setFormData(initialFormData);
  }, [userGroupsOptions]);

  const handleSave = async () => {
    try {
      const body = {
        ...formData,
        shift_1_start: formData.shift_1_start ? `${formData.shift_1_start.getHours()}:${formData.shift_1_start.getMinutes()}` : undefined,
        shift_1_end: formData.shift_1_end ? `${formData.shift_1_end.getHours()}:${formData.shift_1_end.getMinutes()}` : undefined,
        shift_2_start: formData.shift_2_start ? `${formData.shift_2_start.getHours()}:${formData.shift_2_start.getMinutes()}` : undefined,
        shift_2_end: formData.shift_2_end ? `${formData.shift_2_end.getHours()}:${formData.shift_2_end.getMinutes()}` : undefined,
        shift_3_start: formData.shift_3_start ? `${formData.shift_3_start.getHours()}:${formData.shift_3_start.getMinutes()}` : undefined,
        shift_3_end: formData.shift_3_end ? `${formData.shift_3_end.getHours()}:${formData.shift_3_end.getMinutes()}` : undefined,
        nurses_selection: formData.nurses_selection?.map((item) => item.value),
        cna_selection: formData.cna_selection?.map((item) => item.value),
        nurses_hours_per_shift: formData.nurses_hours.value,
        nurses_minutes_per_shift: formData.nurses_minutes.value,
        cna_hours_per_shift: formData.cna_hours.value,
        cna_minutes_per_shift: formData.cna_minutes.value,
        max_working_hours: Number(formData.max_working_hours),
        max_working_hours_period: formData.max_working_hours_period?.value,
        is_tabula_sync_on: formData.is_tabula_sync_on,
      };

      const response = await updateFacility(body, facility.id);

      if (response && response.id) {
        setFacility(response);
        toast(`The settings were updated successfully`, {
          icon: () => <img src="/images/full-check-circle.svg" />,
          closeButton: ({ closeToast }) => (
            <button onClick={closeToast} className="btn btn-icon close-alert-btn">
              <img src="/images/x-icon.svg" />
            </button>
          ),
          className: 'slot-deleted-alert',
          toastClassName: 'slot-deleted-alert-wrapper',
          autoClose: 5000,
          hideProgressBar: false,
          progress: undefined,
          theme: 'light',
          closeOnClick: true,
          pauseOnHover: true,
          type: 'success',
        });
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="facility-settings">
      <div className="facility-settings-item">
        <div className="facility-settings-item-label">
          <h3>Allow CNA assignments module</h3>
          <p>Please, specify if the users with according permission have access to the module</p>
        </div>
        <div className="facility-settings-item-inputs facility-settings-item-inputs-checkbox">
          <div className="form-control-checkbox">
            <input
              name="allow_cna_module"
              type="checkbox"
              checked={formData.allow_cna_module}
              onChange={(e) => setFormData((state) => ({ ...state, allow_cna_module: !state.allow_cna_module }))}
            />
            <span>Allow</span>
          </div>
        </div>
      </div>
      <div className="facility-settings-item">
        <div className="facility-settings-item-label">
          <h3>Allow Bedboard module</h3>
          <p>Please, specify if the users with according permission have access to the module</p>
        </div>
        <div className="facility-settings-item-inputs facility-settings-item-inputs-checkbox">
          <div className="form-control-checkbox">
            <input
              name="allow_bedboard_module"
              type="checkbox"
              checked={formData.allow_bedboard_module}
              onChange={(e) => setFormData((state) => ({ ...state, allow_bedboard_module: !state.allow_bedboard_module }))}
            />
            <span>Allow</span>
          </div>
        </div>
      </div>
      <p className="facility-settings-label">
        <img src="/images/info-icon.png" alt="info" />
        Use these settings to ensure proper staffing and maintain control over shift assignments.
      </p>
      <div className="facility-settings-item">
        <div className="facility-settings-item-label">
          <h3>Specify facility shifts</h3>
          <p>Please define the time range for each shift to ensure accurate scheduling and operations.</p>
        </div>
        <div className="facility-settings-item-inputs facility-setting-shift-time">
          <div className="d-flex time-select mb-2">
            <div className="form-item form-item-select form-item-time">
              <label className="form-label">1st Shift</label>
              <DatePicker
                selected={formData.shift_1_start}
                className={'form-control'}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption=""
                placeholderText={'Choose Time'}
                dateFormat="h:mm aa"
                onChange={(date) => setFormData((state) => ({ ...state, shift_1_start: date }))}
              />
            </div>
            <div className="shift-time-delimiter-wrapper">
              <svg width="17" height="2" viewBox="0 0 17 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 1H15.5" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="form-item form-item-select form-item-time">
              <label className="form-label"> </label>
              <DatePicker
                selected={formData.shift_1_end}
                className={'form-control'}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption=""
                placeholderText={'Choose Time'}
                dateFormat="h:mm aa"
                onChange={(date) => setFormData((state) => ({ ...state, shift_1_end: date }))}
              />
            </div>
          </div>
          <div className="d-flex time-select mb-2">
            <div className="form-item form-item-select form-item-time">
              <label className="form-label">2nd Shift</label>
              <DatePicker
                selected={formData.shift_2_start}
                className={'form-control'}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption=""
                placeholderText={'Choose Time'}
                dateFormat="h:mm aa"
                onChange={(date) => setFormData((state) => ({ ...state, shift_2_start: date }))}
              />
            </div>
            <div className="shift-time-delimiter-wrapper">
              <svg width="17" height="2" viewBox="0 0 17 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 1H15.5" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="form-item form-item-select form-item-time">
              <label className="form-label"> </label>
              <DatePicker
                selected={formData.shift_2_end}
                className={'form-control'}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption=""
                placeholderText={'Choose Time'}
                dateFormat="h:mm aa"
                onChange={(date) => setFormData((state) => ({ ...state, shift_2_end: date }))}
              />
            </div>
          </div>
          <div className="d-flex time-select mb-2">
            <div className="form-item form-item-select form-item-time">
              <label className="form-label">3rd Shift</label>
              <DatePicker
                selected={formData.shift_3_start}
                className={'form-control'}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption=""
                placeholderText={'Choose Time'}
                dateFormat="h:mm aa"
                onChange={(date) => setFormData((state) => ({ ...state, shift_3_start: date }))}
              />
            </div>
            <div className="shift-time-delimiter-wrapper">
              <svg width="17" height="2" viewBox="0 0 17 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 1H15.5" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="form-item form-item-select form-item-time">
              <label className="form-label"> </label>
              <DatePicker
                selected={formData.shift_3_end}
                className={'form-control'}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption=""
                placeholderText={'Choose Time'}
                dateFormat="h:mm aa"
                onChange={(date) => setFormData((state) => ({ ...state, shift_3_end: date }))}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="facility-settings-item">
        <div className="facility-settings-item-label">
          <h3>Staff Selection Settings</h3>
          <p>Choose specific user groups from which employees will be eligible to be assigned to shifts.</p>
        </div>
        <div className="facility-settings-item-inputs">
          <div className="form-item form-item-select nurses-select-wrapper">
            <div className="nurses-select">
              <label className="form-label">Nurses selection</label>
              <MultiSelect
                menuPlacement="top"
                options={userGroupsOptions}
                selectClassName="nurses-selection"
                value={formData.nurses_selection}
                placeholder="Choose one or several Groups"
                onChange={(e) => setFormData((state) => ({ ...state, nurses_selection: e }))}
              />
            </div>
            <div className="nurses-hours-select">
              <label className="form-label">Hours per shift</label>
              <Select
                isSearchable={false}
                hideSelectedOptions={false}
                options={SCHEDULING_HOURS_OPTIONS}
                value={formData.nurses_hours}
                onChange={(e) => setFormData((state) => ({ ...state, nurses_hours: e }))}
                className="form-control-select"
                classNamePrefix="react-select"
              />
            </div>
            <div className="nurses-minutes-select">
              <label className="form-label">Minutes per shift</label>
              <Select
                isSearchable={false}
                hideSelectedOptions={false}
                options={SCHEDULING_MINUTES_OPTIONS}
                value={formData.nurses_minutes}
                onChange={(e) => setFormData((state) => ({ ...state, nurses_minutes: e }))}
                className="form-control-select"
                classNamePrefix="react-select"
              />
            </div>
            {formData.nurses_selection.length > 0 && (
              <>
                <ul className="nurses-selection-list">
                  {formData.nurses_selection.map((item, key) => (
                    <li key={key}>
                      {item.label}
                      <button
                        className="btn btn-icon"
                        onClick={() =>
                          setFormData((state) => ({
                            ...state,
                            nurses_selection: state.nurses_selection.filter((nurse) => nurse.value !== item.value),
                          }))
                        }
                      >
                        <img src="/images/x-circle.svg" alt="" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="form-item form-item-select cna-select-wrapper">
            <div className="cna-select">
              <label className="form-label">CNA selection</label>
              <MultiSelect
                options={userGroupsOptions}
                menuPlacement="top"
                selectClassName="nurses-selection"
                value={formData.cna_selection}
                placeholder="Choose one or several Groups"
                onChange={(e) => setFormData((state) => ({ ...state, cna_selection: e }))}
              />
            </div>
            <div className="cna-hours-select">
              <label className="form-label">Hours per shift</label>
              <Select
                isSearchable={false}
                hideSelectedOptions={false}
                options={SCHEDULING_HOURS_OPTIONS}
                value={formData.cna_hours}
                onChange={(e) => setFormData((state) => ({ ...state, cna_hours: e }))}
                className="form-control-select"
                classNamePrefix="react-select"
              />
            </div>
            <div className="cna-minutes-select">
              <label className="form-label">Minutes per shift</label>
              <Select
                isSearchable={false}
                hideSelectedOptions={false}
                options={SCHEDULING_MINUTES_OPTIONS}
                value={formData.cna_minutes}
                onChange={(e) => setFormData((state) => ({ ...state, cna_minutes: e }))}
                className="form-control-select"
                classNamePrefix="react-select"
              />
            </div>
            {formData.cna_selection.length > 0 && (
              <>
                <ul className="nurses-selection-list">
                  {formData.cna_selection.map((item, key) => (
                    <li key={key}>
                      {item.label}
                      <button
                        className="btn btn-icon"
                        onClick={() =>
                          setFormData((state) => ({
                            ...state,
                            cna_selection: state.cna_selection.filter((cna) => cna.value !== item.value),
                          }))
                        }
                      >
                        <img src="/images/x-circle.svg" alt="" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="facility-settings-item">
        <div className="facility-settings-item-label">
          <h3>Regular Working Hours</h3>
          <p>Set the maximum number of hours an employee can be scheduled to work.</p>
        </div>
        <div className="facility-settings-item-inputs">
          <div className="d-flex max-working-hours" style={{ gap: '12px' }}>
            <div className="form-item form-item-max-working-hours" style={{ flexGrow: 1, width: 'unset' }}>
              <label className="form-label">Max Hours</label>
              <input
                type="number"
                min={0}
                className="form-control"
                value={formData.max_working_hours}
                onChange={(e) => setFormData((state) => ({ ...state, max_working_hours: e.target.value }))}
              />
            </div>
            <div className="form-item form-item-select form-item-max-working-hours-period">
              <label className="form-label">Period</label>
              <Select
                isSearchable={false}
                hideSelectedOptions={false}
                options={MAX_WORKING_HOURS_PERIOD_OPTIONS}
                value={formData.max_working_hours_period}
                onChange={(e) => setFormData((state) => ({ ...state, max_working_hours_period: e }))}
                className="form-control-select"
                classNamePrefix="react-select"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="facility-settings-item">
        <div className="facility-settings-item-label">
          <h3>Residents synchronization with Tabula</h3>
          <p>
            When active, manual entry, Excel imports, and field editing are restricted to ensure your data stays consistent with the
            external source.
          </p>
        </div>
        <div className="facility-settings-item-inputs facility-settings-item-inputs-checkbox">
          <div className="form-settings-inputs">
            <div className="d-flex" style={{ gap: '48px' }}>
              <div className={`form-control-radio`}>
                <input
                  name="is_tabula_sync_on"
                  type="radio"
                  disabled={!facility.tabula_sync_id}
                  onChange={() => setFormData((state) => ({ ...state, is_tabula_sync_on: true }))}
                  checked={formData.is_tabula_sync_on && facility.tabula_sync_id}
                />
                <span>On</span>
              </div>
              <div className={`form-control-radio`}>
                <input
                  name="is_tabula_sync_on"
                  type="radio"
                  disabled={!facility.tabula_sync_id}
                  onChange={() => setFormData((state) => ({ ...state, is_tabula_sync_on: false }))}
                  checked={!formData.is_tabula_sync_on || !facility.tabula_sync_id}
                />
                <span>Off</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="facility-settings-item">
        <div className="facility-settings-item-label"></div>
        <div className="facility-settings-action">
          <button className="btn" onClick={() => handleSave(formData)}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
